import {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { motion } from 'framer-motion';
import { Outlet } from 'react-router-dom';
import { SPRING_LAYOUT } from '@/components/ui/beui/lib/ease';
import { TopBar } from './TopBar';
import { TabStrip } from './TabStrip';

const AI_DOCK_WIDTH = 380;

interface AIDockValue {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  /** Element the chat panel portals into, inside the animated dock column. */
  slot: HTMLElement | null;
}

const AIDockContext = createContext<AIDockValue | null>(null);

/**
 * Shell-level AI dock state. Null when the editor renders outside the shell
 * (the /demo route), where the panel falls back to an inline dock.
 */
export function useAIDock() {
  return useContext(AIDockContext);
}

/**
 * Editor layout (Layout A + header): the dark top bar and tab strip stay for
 * coherence with the workspace, with the full-bleed editor below. No sidebar —
 * editing is a focused mode.
 *
 * The AI chat dock is a column outside the editor card: when it opens, the
 * card (tabs + canvas) shrinks and the panel slides into the freed space,
 * matching the card's height. The top bar stays full width. The editor page
 * owns the panel content and portals it into the dock's slot.
 */
export function EditorLayout() {
  const [aiPanelOpen, setAIPanelOpen] = useState(false);
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  return (
    <AIDockContext.Provider value={{ open: aiPanelOpen, setOpen: setAIPanelOpen, slot }}>
      <div className="flex h-screen w-full flex-col gap-2 overflow-hidden bg-[var(--topbar)] p-2">
        <TopBar />
        <div className="flex min-h-0 flex-1">
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-sm">
            <TabStrip />
            <div className="min-h-0 flex-1">
              <Outlet />
            </div>
          </div>
          <motion.aside
            initial={false}
            animate={{
              width: aiPanelOpen ? '100%' : 0,
              marginLeft: aiPanelOpen ? 8 : 0,
            }}
            transition={SPRING_LAYOUT}
            className="min-h-0 shrink-0 overflow-hidden"
            style={{ maxWidth: AI_DOCK_WIDTH }}
          >
            <div
              ref={setSlot}
              className="h-full w-full overflow-hidden rounded-xl border border-border bg-surface-secondary shadow-sm"
            />
          </motion.aside>
        </div>
      </div>
    </AIDockContext.Provider>
  );
}
